import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Clock, Tag, BookOpen, ArrowRight,
  FileText, Lightbulb, Cpu, Megaphone, Loader2
} from 'lucide-react';
import DOMPurify from 'dompurify';
import PageLayout from '@/components/layout/PageLayout';
import ShareButtons from '@/components/ShareButtons';
import { SocialImage, blogCardSrc } from '@/components/SocialImage';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  regulatory: { label: 'Regulatory Updates', color: '#0066CC', icon: FileText },
  'thought-leadership': { label: 'Thought Leadership', color: '#14b8a6', icon: Lightbulb },
  technical: { label: 'Technical', color: '#8b5cf6', icon: Cpu },
  product: { label: 'Product Updates', color: '#f59e0b', icon: Megaphone },
};

/* Simple Markdown-to-JSX renderer for blog content */
const MarkdownContent = ({ content }) => {
  if (!content) return null;

  const lines = content.split('\n');
  const elements = [];
  let i = 0;
  let tableRows = [];
  let inTable = false;

  const renderInline = (text) => {
    // Bold
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, idx) =>
      idx % 2 === 1 ? <strong key={`b-${part.slice(0, 20)}`} className="font-semibold text-taxera-charcoal">{part}</strong> : part
    );
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) {
      if (inTable) {
        elements.push(<MarkdownTable key={`table-${i}`} rows={tableRows} />);
        tableRows = [];
        inTable = false;
      }
      i++;
      continue;
    }

    // Table rows
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      if (trimmed.includes('---')) { i++; continue; } // separator row
      inTable = true;
      tableRows.push(
        trimmed.split('|').filter(Boolean).map(c => c.trim())
      );
      i++;
      continue;
    }
    if (inTable) {
      elements.push(<MarkdownTable key={`table-${i}`} rows={tableRows} />);
      tableRows = [];
      inTable = false;
    }

    // H2
    if (trimmed.startsWith('## ')) {
      elements.push(
        <h2 key={`h2-${trimmed.slice(3, 30)}`} className="font-heading text-xl md:text-2xl font-bold text-taxera-charcoal mt-10 mb-4">
          {trimmed.slice(3)}
        </h2>
      );
      i++; continue;
    }

    // H3
    if (trimmed.startsWith('### ')) {
      elements.push(
        <h3 key={`h3-${trimmed.slice(4, 30)}`} className="font-heading text-lg font-bold text-taxera-charcoal mt-8 mb-3">
          {trimmed.slice(4)}
        </h3>
      );
      i++; continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="space-y-2 my-4 pl-0 list-none">
          {items.map((item, idx) => (
            <li key={`ol-${item.slice(0, 25)}`} className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-taxera-teal/10 text-taxera-teal text-xs font-bold flex items-center justify-center mt-0.5">
                {idx + 1}
              </span>
              <span className="text-base text-slate-700 leading-relaxed">{renderInline(item)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Unordered list
    if (trimmed.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="space-y-2 my-4 pl-0 list-none">
          {items.map((item, idx) => (
            <li key={`ul-${item.slice(0, 25)}`} className="flex items-start gap-3">
              <span className="text-base text-slate-700 leading-relaxed">{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={`p-${trimmed.slice(0, 25)}`} className="text-base text-slate-700 leading-relaxed my-4">
        {renderInline(trimmed)}
      </p>
    );
    i++;
  }

  // Flush remaining table
  if (inTable && tableRows.length) {
    elements.push(<MarkdownTable key="table-end" rows={tableRows} />);
  }

  return <>{elements}</>;
};

const MarkdownTable = ({ rows }) => {
  if (!rows.length) return null;
  const header = rows[0];
  const body = rows.slice(1);

  return (
    <div className="my-6 overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50">
            {header.map((cell, i) => (
              <th key={`th-${cell.slice(0, 20)}`} className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide border-b border-slate-200">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={`tr-${row[0]?.slice(0, 20) || ri}`} className="border-b border-slate-100 last:border-0">
              {row.map((cell, ci) => (
                <td key={`td-${cell.slice(0, 20)}`} className="px-4 py-3 text-slate-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ShareCardAside = ({ article, catMeta, onMissing }) => (
  <aside className="lg:sticky lg:top-28 space-y-6" data-testid="post-sidebar">
    <div className="flex items-center gap-3 p-4 rounded-2xl border border-slate-200 bg-white" data-testid="sidebar-author">
      <div className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: catMeta?.color || '#666' }}>
        {article.author?.name?.split(' ').map(n => n[0]).join('')}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-taxera-charcoal truncate">{article.author?.name}</div>
        <div className="text-xs text-slate-400 truncate">{article.author?.role}</div>
      </div>
    </div>
    <div className="max-w-[320px]" data-testid="sidebar-share-card">
      <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-2">Share this card</div>
      <SocialImage
        src={blogCardSrc(article)}
        alt={article.title}
        width={320}
        height={320}
        className="block w-full h-auto aspect-square rounded-2xl border border-slate-100 bg-slate-100 mb-3"
        testId="sidebar-social-card"
        onMissing={onMissing}
      />
      <ShareButtons url={`${window.location.origin}/blog/${article.slug}`} title={article.title} summary={article.excerpt} compact />
    </div>
  </aside>
);

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardMissing, setCardMissing] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await fetch(`${API}/api/blog/articles/${slug}`, { redirect: 'manual' });
        if (res.type === 'opaqueredirect' || res.status === 301 || res.status === 302) {
          // API returned a redirect — this is a consolidated article
          const location = res.headers.get('Location');
          if (location) {
            navigate(location, { replace: true });
          } else {
            navigate('/blog', { replace: true });
          }
          return;
        }
        if (res.ok) {
          const data = await res.json();
          setArticle(data);
          window.__BLOG_META = {
            title: `${data.title} | Taxera`,
            description: (data.excerpt || '').slice(0, 160),
            image: data.image_url || undefined,
          };
        } else {
          navigate('/blog', { replace: true });
        }
      } catch {
        navigate('/blog', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-taxera-blue" />
        </div>
      </PageLayout>
    );
  }

  if (!article) return null;

  const catMeta = CATEGORY_META[article.category];
  const CatIcon = catMeta?.icon;
  const showAside = blogCardSrc(article) && !cardMissing;
  const pageUrl = `https://taxera.tech/blog/${slug}`;

  // Blog Article JSON-LD for SEO + AI search
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary || article.excerpt || '',
    author: { '@type': 'Person', name: article.author || 'Paul Antunes' },
    publisher: {
      '@type': 'Organization',
      name: 'Taxera Technologies GmbH',
      url: 'https://taxera.tech',
    },
    datePublished: article.created_at || article.published_at,
    dateModified: article.updated_at || article.created_at,
    url: pageUrl,
    mainEntityOfPage: pageUrl,
    articleSection: catMeta?.label || article.category,
    keywords: (article.tags || []).join(', '),
  };

  // Set page title + description dynamically
  if (typeof document !== 'undefined') {
    document.title = `${article.title} — Taxera Blog`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', article.summary || article.excerpt || '');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', article.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', article.summary || article.excerpt || '');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', pageUrl);
    if (article.image_url) {
      document.querySelector('meta[property="og:image"]')?.setAttribute('content', article.image_url);
      document.querySelector('meta[name="twitter:image"]')?.setAttribute('content', article.image_url);
    }
  }

  return (
    <PageLayout>
      {/* Article JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(articleSchema), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }) }} />
      <div className={showAside ? 'max-w-6xl mx-auto px-6 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12' : ''}>
      <article className={showAside ? 'max-w-3xl py-8 min-w-0' : 'max-w-3xl mx-auto px-6 py-8'} data-testid="blog-post">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-taxera-blue transition-colors mb-8"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          All articles
        </Link>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-5">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-2xs font-bold uppercase tracking-wide text-white"
              style={{ backgroundColor: catMeta?.color || '#666' }}
              data-testid="post-category"
            >
              {CatIcon && <CatIcon className="w-3 h-3" />}
              {catMeta?.label}
            </span>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {article.reading_time} min read
            </span>
          </div>

          <h1 className="font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-taxera-charcoal leading-tight mb-5" data-testid="post-title">
            {article.title}
          </h1>

          <p className="text-lg text-slate-500 leading-relaxed mb-6" data-testid="post-excerpt">
            {article.excerpt}
          </p>

          {/* Author & Tags */}
          <div className="flex items-center justify-between flex-wrap gap-4 pb-8 border-b border-slate-200 mb-8">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                style={{ backgroundColor: catMeta?.color || '#666' }}
              >
                {article.author?.name?.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <div className="text-sm font-semibold text-taxera-charcoal" data-testid="post-author">{article.author?.name}</div>
                <div className="text-xs text-slate-400">{article.author?.role}</div>
                <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                  {article.created_at && <span data-testid="post-publish-date">Published {new Date(article.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                  {article.updated_at && article.updated_at !== article.created_at && <span data-testid="post-updated-date"> · Updated {new Date(article.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                </div>
              </div>
            </div>
            {article.tags && (
              <div className="flex flex-wrap gap-1.5">
                {article.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 text-xs text-slate-500 rounded-full border border-slate-100">
                    <Tag className="w-2.5 h-2.5" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Article Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="prose-taxera"
          data-testid="post-content"
        >
          <MarkdownContent content={article.content} />
        </motion.div>

        {/* Share */}
        <div className="mt-10 pt-8 border-t border-slate-200">
          <ShareButtons
            url={`${window.location.origin}/blog/${article.slug}`}
            title={article.title}
            summary={article.excerpt}
          />
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 bg-slate-900 rounded-3xl p-8 md:p-10 relative overflow-hidden"
          data-testid="post-cta"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_80%,rgba(20,163,184,0.1)_0%,transparent_50%)]" />
          <div className="relative z-10 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
              Ready to assess your compliance posture?
            </h3>
            <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
              Take our free diagnostic — 3 minutes to understand where you stand and where you're exposed.
            </p>
            <Link
              to="/evaluate"
              className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105"
              data-testid="post-cta-btn"
            >
              Take the Diagnostic
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>

        {/* Back to blog */}
        <div className="mt-12 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-taxera-blue transition-colors"
            data-testid="bottom-back-to-blog"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to all articles
          </Link>
        </div>
      </article>
      {showAside && (
        <div className="pb-12 lg:py-8">
          <ShareCardAside article={article} catMeta={catMeta} onMissing={() => setCardMissing(true)} />
        </div>
      )}
      </div>
    </PageLayout>
  );
};

export default BlogPostPage;
